import React, { Component, ErrorInfo, ReactNode } from "react";
import { debugError } from "../../utils/debugLog";
import { attemptChunkLoadRecovery, isLikelyChunkLoadError } from "./chunkLoadRecovery";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  isChunkError: boolean;
  error?: Error;
}

class ChunkLoadErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, isChunkError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      isChunkError: isLikelyChunkLoadError(error),
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    debugError?.("ChunkLoadErrorBoundary caught an error:", error, errorInfo);

    // If this looks like a chunk-load error (e.g. after a deploy), reload once to get the new build.
    if (attemptChunkLoadRecovery(error)) {
      return;
    }
  }

  handleRefresh = () => {
    attemptChunkLoadRecovery(this.state.error ?? "chunk load error", { force: true });
  };

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    if (this.state.isChunkError) {
      return (
        <div className="min-h-[50vh] flex items-center justify-center">
          <div className="max-w-lg text-center bg-gray-900/60 border border-gray-700 rounded-2xl p-8">
            <h2 className="text-xl sm:text-2xl font-bold text-white mb-3">
              Update available
            </h2>
            <p className="text-gray-300 mb-6">
              The site has been updated in the background. Refresh to load the latest
              version.
            </p>
            <button
              type="button"
              onClick={this.handleRefresh}
              className="bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded-xl font-semibold transition-colors"
            >
              Refresh page
            </button>
          </div>
        </div>
      );
    }

    return (
      <div className="min-h-[50vh] flex items-center justify-center">
        <div className="max-w-lg text-center bg-gray-900/60 border border-gray-700 rounded-2xl p-8">
          <h2 className="text-xl sm:text-2xl font-bold text-white mb-3">
            Something went wrong
          </h2>
          <p className="text-gray-300 mb-6">
            Please try refreshing the page.
          </p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="bg-gray-800 hover:bg-gray-700 text-white px-6 py-3 rounded-xl font-semibold transition-colors"
          >
            Refresh
          </button>
        </div>
      </div>
    );
  }
}

export default ChunkLoadErrorBoundary;

